import type { TeamMember } from '@qlk/shared';
import { useState } from 'react';

import { HopXacNhan, Nut, ngayGio } from '../../../ui/index.js';
import { useLamMoiLoiMoi, useThuHoiLoiMoi } from '../api.js';
import { ThongBaoMail } from './KhoiLinkDatMatKhau.js';

/**
 * Khối trên thẻ của người **đang ở trạng thái "đã mời"**. Chỉ quản trị thấy.
 *
 * Có ba việc quản trị cần làm khi người được mời chưa vào được: xem link còn
 * sống đến bao giờ, lấy link để gửi tay (Zalo, nhắn miệng), và gửi lại thư.
 *
 * "Tạo lại link" và "Gửi lại email mời" đều phát link **mới** và cho link cũ
 * hết hạn ngay. Không có nút "xem lại link cũ": database chỉ giữ bản băm
 * SHA-256 của token nên link gốc không dựng lại được — đổi lại, người đọc được
 * database cũng không mượn được link của ai.
 *
 * Việc thứ tư: **thu hồi lời mời** khi mời sai người hoặc người đó không vào
 * nữa. Nó xoá hẳn tài khoản chưa kích hoạt (xem hộp xác nhận), khác hẳn "khoá"
 * dùng cho người đã vào — nên nút để riêng, màu đỏ.
 */
export function KhoiNguoiDaMoi({ thanhVien }: { thanhVien: TeamMember }) {
  const lamMoi = useLamMoiLoiMoi();
  const thuHoi = useThuHoiLoiMoi();
  const [hoiThuHoi, setHoiThuHoi] = useState(false);

  const hetHan = thanhVien.inviteExpiresAt;
  const dangChay = lamMoi.isPending;

  return (
    <div className="mt-4 rounded-[10px] border border-vien bg-nen p-3">
      <p className="text-phu font-medium text-chu-phu">Lời mời</p>
      <p className="mt-1 text-phu text-chu-mo">
        {hetHan
          ? `Link mời hết hạn ${ngayGio(hetHan)}.`
          : 'Không còn link nào hiệu lực — tạo lại link rồi gửi cho người này.'}
      </p>

      <div className="mt-3 flex flex-wrap gap-2">
        <Nut
          kieu="phu"
          dangChay={dangChay}
          onClick={() => {
            lamMoi.mutate({ id: thanhVien.id, guiMail: false });
          }}
        >
          Tạo lại link
        </Nut>
        <Nut
          kieu="phu"
          dangChay={dangChay}
          onClick={() => {
            lamMoi.mutate({ id: thanhVien.id, guiMail: true });
          }}
        >
          Gửi lại email mời
        </Nut>
        <Nut
          kieu="nguy-hiem"
          onClick={() => {
            setHoiThuHoi(true);
          }}
        >
          Thu hồi lời mời
        </Nut>
      </div>

      {lamMoi.isError && (
        <p role="alert" className="mt-3 text-phu text-bad-chu">
          {lamMoi.error.message}
        </p>
      )}

      {lamMoi.data && (
        <ThongBaoMail
          ketQua={lamMoi.data}
          tieuDe={
            lamMoi.variables?.guiMail ? 'Đã phát link mới và gửi thư' : 'Đã phát link mời mới'
          }
        />
      )}

      {hoiThuHoi && (
        <HopXacNhan
          title="Thu hồi lời mời?"
          nhanXacNhan="Thu hồi lời mời"
          nguyHiem
          dangChay={thuHoi.isPending}
          loi={thuHoi.error?.message ?? null}
          onXacNhan={() => {
            thuHoi.mutate(
              { id: thanhVien.id },
              {
                onSuccess: () => {
                  setHoiThuHoi(false);
                },
              },
            );
          }}
          onDong={() => {
            setHoiThuHoi(false);
          }}
        >
          <p>
            Link mời của <strong className="text-chu">{thanhVien.email}</strong> chết ngay, ai đang
            giữ link cũng không dùng được nữa.
          </p>
          <p>
            Tài khoản chưa kích hoạt này bị <strong className="text-chu">xoá hẳn</strong> — người đó
            chưa đặt mật khẩu nên chưa có dữ liệu gì trong hệ thống. Email được giải phóng, mời lại
            được ngay.
          </p>
          <p>Muốn giữ tài khoản mà chỉ chặn đăng nhập thì dùng "Khoá tài khoản", không thu hồi.</p>
        </HopXacNhan>
      )}
    </div>
  );
}
